import { Injectable, computed, signal } from '@angular/core';

// Bridge envelope constants duplicated from
// feature/code-server-overlay/code-server-overlay.bridge.ts rather than
// imported: this module lives in core/, which must not depend on a feature/
// module (code-server-overlay already depends on core/ for this very
// service) — spec-welcome-mode.md §8.3. Consolidating the envelope helpers
// into a shared lower layer is an owner call, out of this slice's scope.
const AGENT_AUTH_BRIDGE_CHANNEL = '@zerops/zcp-agent-auth-bridge';

export type EmbedWizardMode = 'onboarding' | 'standard';

interface SetModeBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'set-mode';
  eventId: string;
  createdAt: number;
  mode: EmbedWizardMode;
}

interface LaunchAgentBridgeMessage {
  channel: typeof AGENT_AUTH_BRIDGE_CHANNEL;
  version: 1;
  type: 'launch-agent';
  eventId: string;
  createdAt: number;
  agentId: string;
}

function buildSetModeMessage(mode: EmbedWizardMode, eventId: string, createdAt: number): SetModeBridgeMessage {
  return { channel: AGENT_AUTH_BRIDGE_CHANNEL, version: 1, type: 'set-mode', eventId, createdAt, mode };
}

function buildLaunchAgentMessage(agentId: string, eventId: string, createdAt: number): LaunchAgentBridgeMessage {
  return { channel: AGENT_AUTH_BRIDGE_CHANNEL, version: 1, type: 'launch-agent', eventId, createdAt, agentId };
}

// `crypto.randomUUID()` is unsupported in the jsdom test environment (only
// `getRandomValues` is) despite being universal in real browsers — building
// the UUIDv4 from `getRandomValues` directly sidesteps the gap everywhere
// instead of special-casing tests. Matches the bridge validator's `UUID_V4`
// regex (code-server-overlay.bridge.ts): version nibble fixed to 4, variant
// nibble fixed to 8-b.
function generateEventId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// The 30s absolute cap on a minted launch intent (spec-welcome-mode.md
// §8.1): the ONLY path left that reaches `failed` from `launching` — and
// only when it fires having never posted a single launch attempt (no
// retained embed address ever, no announce). Once an attempt has posted,
// this cap converges on degraded completion instead, same as an explicit
// `launch-failed` and the 5s reveal timer below.
const LAUNCH_INTENT_TIMEOUT_MS = 30_000;

// The 5s reveal window (spec-welcome-mode.md §8.1 "degraded reveal"):
// armed — or restarted — by every launch attempt that actually posts to the
// embed. Once a launch has been posted, the workspace itself is the
// recovery surface, so a silent outcome this soon after is not worth
// holding the layer for.
const REVEAL_WINDOW_MS = 5_000;

/**
 * A promo code carried in from a conference coin, shown once before the roster.
 * Declared here rather than imported from `core/promo-code-base` on purpose:
 * that module already imports this one, so importing back would be a cycle.
 *
 * `state` is what HAPPENED to the code, not what kind of code it is — the two
 * are not the same, and conflating them once made the wizard announce "promo
 * credit added" for a code that had merely been carried through a login. Only
 * `redeemed` has an amount, because only a redeemed code has one.
 *
 * `parked` and `needs-top-up` are likewise NOT the same and must not be merged
 * back: a parked code is held by the backend and lands on whatever the first
 * payment turns out to be, while a `needs-top-up` code is only ours, in a
 * cookie, and applies solely if the user attaches it to a payment. Telling the
 * second group what is true of the first would have them pay and get nothing.
 *
 * `confirming` is the gap between Stripe accepting the card and our own backend
 * hearing about it over the webhook: money is committed, the amount is not
 * known yet. It carries no promise of a deadline because we have none to give.
 */
export interface PromoReveal {
  code: string;
  state: 'redeemed' | 'parked' | 'needs-top-up' | 'awaiting-redeem' | 'confirming';
  amount?: string;
}

export type WizardState =
  | 'idle'
  | 'claiming'
  | 'promo'
  | 'picking'
  | 'authorizing'
  | 'launching'
  | 'done'
  | 'failed';

function isWizardActive(state: WizardState): boolean {
  return (
    state === 'claiming'
    || state === 'promo'
    || state === 'picking'
    || state === 'authorizing'
    || state === 'launching'
  );
}

/**
 * Drives the agent-first onboarding wizard (spec-welcome-mode.md §8.1):
 * `claiming` (drain resolving) -> `picking` (static roster shown) ->
 * `authorizing` (existing auth dialog dispatched over the layer, on an
 * unauthorized pick) -> `launching` (intent minted + sent/queued, 30s
 * absolute cap) -> `done`. There is no confirmation stop between auth and
 * launch: `authSucceeded()` — and an already-authorized pick, which skips
 * `authorizing` entirely — mint the launch intent immediately.
 *
 * `done` is reached two ways. The happy path: `agent-ready` matching the
 * held eventId. DEGRADED COMPLETION (`#completeDegraded()`): once a launch
 * attempt has actually posted to the embed, the workspace itself becomes
 * the recovery surface, so an explicit `launch-failed`, the 5s no-outcome
 * reveal timer a successful post arms/restarts, and the 30s cap catching a
 * posted-but-unresolved attempt all converge on the SAME outcome — send
 * `set-mode "standard"` (hands the container its own presentation rules: a
 * live terminal stays visible, an empty workbench reveals the agent panel),
 * then drop the layer exactly like the happy path. `failed` survives only
 * for endings with nothing to reveal: the 30s cap firing having never
 * posted a single attempt (dead/never-reloading embed), or an unreachable
 * auth dialog pre-launch.
 *
 * Auth outcomes are driven by the auth dialog's OWN real signals, never by
 * "the dialog opened": `authSucceeded()` fires on the `markAuthorized` NgRx
 * action (real OAuth completion), `authDialogDismissed()` fires on the
 * dialog closing with no success latched (bounces back to `picking`,
 * keeping the pick), `authDialogUnavailable()` fires when the dialog never
 * became reachable at all.
 *
 * Evolved from the ZCP-pool claim cover (still signal-based transient UI
 * state, shared by the drain effect and the code-server overlay). `prewarm`/
 * `show`/`hide`/`visible`/`prewarmServiceStackId` are the preserved surface
 * carried over from that cover.
 *
 * `ev.source`/origin for the embedded iframe are retained HERE, never in an
 * ngrx action (§8.3) — the only place this service reaches outside its own
 * state is the `postMessage` calls in `#post`.
 */
@Injectable({ providedIn: 'root' })
export class ZcpOnboardWizardService {
  #state = signal<WizardState>('idle');
  state = this.#state.asReadonly();

  // Cover/layer visibility. `done` means the layer already dropped (on
  // `agent-ready`); `failed` stays visible until an explicit `hide()`
  // (the failure state's Continue button) closes it.
  visible = computed(() => {
    const s = this.#state();
    return s !== 'idle' && s !== 'done';
  });

  // Whether the wizard currently owns the FE's `set-mode` directive for an
  // `embed-ready` announce (§4.3/§8.3): exactly one of this signal's owner
  // (the wizard itself, via `onEmbedReady`) or the overlay feature's
  // container-owned default may answer a given announce — the feature gates
  // its own default reply on this being false.
  active = computed(() => isWizardActive(this.#state()));

  // Ids from the static roster (`SUPPORTED_AGENT_TYPES`, rendered directly
  // by the component) already authorized (spec-welcome-mode.md §8.2),
  // sourced from the FE's own userData (`parseAuthorizedAgents`) at
  // `enterPicking` time — so `pickAgent`'s skip below works without waiting
  // on a boot. Exposed read-only so the component can render an authorized
  // badge on the matching tile(s); only `pickAgent` (below) still consults
  // the private signal for the skip-authorizing decision.
  #authorizedAgentIds = signal<ReadonlySet<string>>(new Set());
  authorizedAgentIds = this.#authorizedAgentIds.asReadonly();

  #pickedAgentId = signal<string | undefined>(undefined);
  pickedAgentId = this.#pickedAgentId.asReadonly();

  // The coin's reveal, shown once between `claiming` and the roster. This is
  // the only place the user is ever told what their code was worth, so it takes
  // the screen rather than sharing it.
  #promoReveal = signal<PromoReveal | undefined>(undefined);
  promoReveal = this.#promoReveal.asReadonly();

  // Service-stack id of the ZCP to pre-open. Set by the drain the instant it
  // finds the ZCP (behind the cover, in parallel with the navigation); the
  // global code-server overlay reacts and starts loading the iframe early
  // rather than waiting for the control-plane page to mount, gate, and scroll.
  // Core stores it, the feature reads it — keeps the dependency one-directional.
  #prewarmServiceStackId = signal<string | undefined>(undefined);
  prewarmServiceStackId = this.#prewarmServiceStackId.asReadonly();

  // Identity of the currently embedded iframe's window, retained across
  // announces (a reload re-announces and overwrites this) — never surfaced
  // as a signal / never carried in an ngrx action.
  #retainedSource: MessageEventSource | null = null;
  #retainedOrigin: string | undefined;

  // The current launch intent's identity (minted once per `#beginLaunch()`,
  // reused verbatim on every retry per §4.3) and its 30s absolute-cap timer.
  #launchEventId: string | undefined;
  #launchTimer: ReturnType<typeof setTimeout> | undefined;

  // Whether the current intent has EVER posted successfully (§8.1 degraded
  // reveal) — the arming condition that decides how the 30s cap resolves
  // (`failed` if still false, degraded completion once true) and gates the
  // reveal timer below. Reset by `#clearLaunch()`, same lifecycle as the
  // intent itself.
  #launchAttemptPosted = false;
  // The 5s reveal timer, armed/restarted by every successful post.
  #revealTimer: ReturnType<typeof setTimeout> | undefined;

  prewarm(serviceStackId: string): void {
    this.#prewarmServiceStackId.set(serviceStackId);
  }

  /** Raises the wizard cover. Entry point for the normal (cookie-drain) flow. */
  show(): void {
    this.#state.set('claiming');
  }

  /**
   * `claiming`/`idle` -> `picking`. The roster itself is the static
   * `SUPPORTED_AGENT_TYPES` registry, rendered directly by the component —
   * this service only tracks which of those ids are already authorized
   * (`parseAuthorizedAgents`, parsed from the FE's own userData, §8.1/§8.2).
   * Defaults to none authorized so callers that don't carry auth state yet
   * behave exactly as before this parameter existed. Also the entry point
   * for the §8.2 dev parameter.
   */
  enterPicking(authorizedAgentIds: readonly string[] = []): void {
    this.#clearLaunch();
    this.#authorizedAgentIds.set(new Set(authorizedAgentIds));
    this.#pickedAgentId.set(undefined);
    // A waiting reveal goes first; `dismissPromoReveal` hands over to the
    // roster, which is already staged by the two setters above.
    this.#state.set(this.#promoReveal() ? 'promo' : 'picking');
  }

  /**
   * Hand the wizard a coin reveal. Usually set while the claim is still
   * resolving, so `enterPicking` finds it — but a standalone amount has to be
   * read back from the API, which can land after the roster is already up, so
   * take the screen back when nothing has been picked yet.
   */
  setPromoReveal(reveal: PromoReveal): void {
    this.#promoReveal.set(reveal);

    if (this.#state() === 'picking' && !this.#pickedAgentId()) {
      this.#state.set('promo');
    }
  }

  /**
   * Replace the reveal in place, ONLY while it is the thing on screen. A
   * payment-mode code becomes a real amount the moment the top-up completes,
   * which lands over the websocket seconds later — often while the user is
   * still looking at it. Guarded on the state so a late arrival can never drag
   * someone back out of the roster they have already moved on to.
   */
  updatePromoReveal(reveal: PromoReveal): void {
    if (this.#state() !== 'promo') { return; }
    this.#promoReveal.set(reveal);
  }

  /** Reveal acknowledged — on to the roster. Single-use. */
  dismissPromoReveal(): void {
    if (this.#state() !== 'promo') { return; }
    this.#promoReveal.set(undefined);
    this.#state.set('picking');
  }

  /**
   * Single-select (§8.1): only honored from `picking`. An already-authorized
   * pick (§8.2) skips `authorizing` entirely and launches immediately — the
   * same destination `authSucceeded()` reaches — the repeated dev-loop
   * launch a real test container (every agent typically already authorized)
   * needs, without a redundant OAuth round trip.
   */
  pickAgent(agentId: string): void {
    if (this.#state() !== 'picking') return;
    this.#pickedAgentId.set(agentId);
    if (this.#authorizedAgentIds().has(agentId)) {
      this.#beginLaunch();
      return;
    }
    this.#state.set('authorizing');
  }

  /**
   * Real OAuth completion — the `markAuthorized` NgRx action — only from
   * `authorizing`. Launches immediately: no confirmation stop between auth
   * and launch.
   */
  authSucceeded(): void {
    if (this.#state() !== 'authorizing') return;
    this.#beginLaunch();
  }

  /**
   * The auth dialog closed with no success latched — only from
   * `authorizing`. Bounces back to `picking`, KEEPING the picked agent (the
   * tile still renders selected; re-picking the same or a different agent
   * overwrites it).
   */
  authDialogDismissed(): void {
    if (this.#state() !== 'authorizing') return;
    this.#state.set('picking');
  }

  /** The auth dialog never became reachable at all — only from `authorizing`. */
  authDialogUnavailable(): void {
    if (this.#state() !== 'authorizing') return;
    this.#state.set('failed');
  }

  /**
   * The single launch entry (§8.1), reached from `authSucceeded()` and an
   * already-authorized `pickAgent()`. Mints a fresh launch intent and enters
   * `launching` with its 30s absolute cap — sent immediately if an embed
   * address is already retained, otherwise queued for the next
   * `embed-ready`.
   */
  #beginLaunch(): void {
    this.#launchEventId = generateEventId();
    this.#launchAttemptPosted = false;
    this.#state.set('launching');
    this.#launchTimer = setTimeout(() => this.#onLaunchTimeout(), LAUNCH_INTENT_TIMEOUT_MS);
    if (this.#retainedSource) {
      this.#sendLaunchAttempt();
    }
  }

  /**
   * Routed from the code-server overlay's bridge listener on every
   * `embed-ready` announce (§8.3) — the only place `ev.source`/origin ever
   * reach this service. Re-announces (an embed reload) simply overwrite the
   * retained address. Sends `set-mode "onboarding"` while the wizard is
   * active, then retries the queued launch intent (same eventId, fresh
   * `createdAt`) while `launching`.
   */
  onEmbedReady(source: MessageEventSource, origin: string): void {
    this.#retainedSource = source;
    this.#retainedOrigin = origin;

    const state = this.#state();
    if (isWizardActive(state)) {
      this.#sendMode('onboarding');
    }
    if (state === 'launching') {
      this.#sendLaunchAttempt();
    }
  }

  /** Ready = S2 (§4.3): the command's eventId, matched against the held intent. */
  onAgentReady(eventId: string): void {
    if (this.#state() !== 'launching' || eventId !== this.#launchEventId) return;
    this.#clearLaunch();
    this.#state.set('done');
  }

  /**
   * An explicit container rejection for the current launch (§8.1) — no
   * longer a hard failure: the launch was posted (that's how the container
   * came to reject it), so this converges on the same degraded completion
   * as the 5s reveal timer and a posted-but-unresolved 30s cap.
   */
  onLaunchFailed(eventId: string): void {
    if (this.#state() !== 'launching' || eventId !== this.#launchEventId) return;
    this.#completeDegraded();
  }

  /**
   * The embed address just went stale (overlay reset — closed, or reopened
   * for a different service, §8.3) — clears the retained source/origin and
   * nothing else. Without this a later launch/`onEmbedReady()`
   * could address a window that is no longer the live embed, or a stale
   * "announce seen" could satisfy a launch intent for the wrong service.
   */
  onEmbedGone(): void {
    this.#retainedSource = null;
    this.#retainedOrigin = undefined;
  }

  /** Universal reset: abandons the wizard entirely, back to `idle`. */
  hide(): void {
    this.#clearLaunch();
    this.#state.set('idle');
    this.#authorizedAgentIds.set(new Set());
    this.#pickedAgentId.set(undefined);
    this.#prewarmServiceStackId.set(undefined);
    this.#promoReveal.set(undefined);
  }

  /**
   * 30s absolute cap (§8.1): `failed` only if the intent NEVER managed a
   * single successful post (dead/never-reloading embed) — otherwise the
   * workspace is already reachable, so this converges on degraded
   * completion same as the reveal timer and an explicit `launch-failed`.
   */
  #onLaunchTimeout(): void {
    if (this.#state() !== 'launching') return;
    if (this.#launchAttemptPosted) {
      this.#completeDegraded();
      return;
    }
    this.#clearLaunch();
    this.#state.set('failed');
  }

  /** 5s reveal timer expiry (§8.1): no outcome yet, but a launch was posted — reveal anyway. */
  #onRevealTimeout(): void {
    if (this.#state() !== 'launching') return;
    this.#completeDegraded();
  }

  /**
   * The single degraded-completion path (§8.1), reached from three call
   * sites (the 5s reveal timer, the 30s cap once an attempt posted, and an
   * explicit `launch-failed`): hand the container back its own presentation
   * rules — `set-mode "standard"` — BEFORE dropping the layer, so a live
   * terminal or the agent panel is already what the container shows once
   * this component itself is gone. Order matters: send while the embed
   * address is still retained (`#clearLaunch()` never touches it, but
   * sending after `done` would be pointless busywork either way — this
   * keeps the sequence explicit).
   */
  #completeDegraded(): void {
    this.#sendMode('standard');
    this.#clearLaunch();
    this.#state.set('done');
  }

  #sendLaunchAttempt(): void {
    const agentId = this.#pickedAgentId();
    if (!this.#launchEventId || !agentId) return;
    const posted = this.#post(buildLaunchAgentMessage(agentId, this.#launchEventId, Date.now()));
    if (posted) {
      this.#launchAttemptPosted = true;
      this.#armRevealTimer();
    }
  }

  #armRevealTimer(): void {
    if (this.#revealTimer) {
      clearTimeout(this.#revealTimer);
    }
    this.#revealTimer = setTimeout(() => this.#onRevealTimeout(), REVEAL_WINDOW_MS);
  }

  #sendMode(mode: EmbedWizardMode): void {
    this.#post(buildSetModeMessage(mode, generateEventId(), Date.now()));
  }

  /** Returns whether the message actually posted — never throws. */
  #post(message: SetModeBridgeMessage | LaunchAgentBridgeMessage): boolean {
    if (!this.#retainedSource || !this.#retainedOrigin) return false;
    try {
      (this.#retainedSource as Window).postMessage(message, this.#retainedOrigin);
      return true;
    } catch {
      // Sender navigated away or closed before delivery.
      return false;
    }
  }

  #clearLaunch(): void {
    if (this.#launchTimer) {
      clearTimeout(this.#launchTimer);
      this.#launchTimer = undefined;
    }
    if (this.#revealTimer) {
      clearTimeout(this.#revealTimer);
      this.#revealTimer = undefined;
    }
    this.#launchEventId = undefined;
    this.#launchAttemptPosted = false;
  }
}
